<script lang="ts">
	import type { Mediator, SvelteAsr } from 'types/common'
	import { graphql, type SalesOrder$result, type InitialLoadQuery$result } from '$houdini'
	import { formatCustomerCurrency } from 'utility/customer-helper'
	import { getContext } from 'svelte'
	import { getEventValue } from '@isoftdata/browser-event'

	import Button from '@isoftdata/svelte-button'
	import Input from '@isoftdata/svelte-input'
	import NavTabBar from '@isoftdata/svelte-nav-tab-bar'
	import Select from '@isoftdata/svelte-select'
	import TextArea from '@isoftdata/svelte-textarea'
	import SearchModal from 'components/SearchModal.svelte'
	import type { i18n } from 'types/common'
	const { t: translate } = getContext<i18n>('i18next')

	const mediator = getContext<Mediator>('mediator')

	interface Props {
		asr: SvelteAsr
		salesOrder: SalesOrder$result['salesOrder']
		selectedCustomerDetailTab: 'CUSTOMER_DETAIL' | 'BILLING_AND_SHIPPING'
		salesPersonList: InitialLoadQuery$result['userAccounts']
		shippingMethods: InitialLoadQuery$result['shippingMethods']
		taxItems: InitialLoadQuery$result['taxItems']
		viewOnlyMode?: boolean
		salesOrderChanged?: boolean
	}

	let {
		asr,
		salesOrder = $bindable(),
		selectedCustomerDetailTab = 'CUSTOMER_DETAIL',
		salesPersonList = [],
		shippingMethods = [],
		taxItems = [],
		viewOnlyMode = true,
		salesOrderChanged = $bindable(false),
	}: Props = $props()

	// Disable the editable basic fields whenever the screen is view-only OR no document is loaded
	// (a blank/new sales order should not be editable yet — see EEW-629 review).
	const editingDisabled = $derived(viewOnlyMode || !salesOrder)

	let customerSearchModal: SearchModal = $state()!
	let editShippingAddressMode = $state(false)
	let customerShippingAddresses: { id: number; label: string }[] = $state(generateCustomerShippingAddressList())

	function generateCustomerShippingAddressList(): { id: number; label: string }[] {
		const initialCustomerShippingAddressList: { id: number; label: string }[] = [
			{
				id: 0,
				label: translate('salesOrder.customShippingAddress', 'Custom Shipping Address'),
			},
		]

		if (!salesOrder?.customer?.addresses) {
			return initialCustomerShippingAddressList
		} else {
			return initialCustomerShippingAddressList.concat(salesOrder.customer.addresses)
		}
	}

	async function loadSelectedCustomerDetails(customerId: number) {
		try {
			const { data } = await loadCustomerDetailsQuery.fetch({
				variables: {
					customerId,
					messageFilter: {
						triggers: ['SALES_ORDER', 'GENERAL'],
						display: ['BOTH', 'SHOW'],
						active: true,
					},
				},
			})

			if (!data) {
				throw new Error('Customer not found.')
			}

			// TODO for next version: if sales order is null, create a new sales order object with all default values
			if (salesOrder) {
				salesOrder = {
					...salesOrder,
					customer: data.customer,
				}
			}
		} catch (error: any) {
			mediator.publish('showMessage', {
				type: 'danger',
				heading: translate('salesOrder.failedToLoadCustomerDetails', 'Failed To Load Customer Details'),
				message: error.message,
				time: false,
			})
		}
	}

	const loadCustomerDetailsQuery = graphql(`
		query LoadCustomerDetails($customerId: UInt!, $messageFilter: MessageFilter) {
			customer(id: $customerId) {
				id
				companyName
				balance
				accountLimit
				addresses {
					id
					label
				}
				messages(filter: $messageFilter) {
					id
					text
					type
					trigger
					important
				}
			}
		}
	`)
</script>

<div class="card h-100">
	<div class="card-header">
		<NavTabBar
			tabs={[
				{ name: 'CUSTOMER_DETAIL', title: translate('salesOrder.basicInfo', 'Basic Info') },
				{ name: 'BILLING_AND_SHIPPING', title: translate('salesOrder.billingAndShipping', 'Billing And Shipping') },
			]}
			bind:selectedTab={selectedCustomerDetailTab}
			tabChange={() => asr.go(null, { selectedCustomerDetailTab }, { inherit: true })}
		/>
	</div>
	<div class="card-body">
		{#if selectedCustomerDetailTab === 'CUSTOMER_DETAIL'}
			<div class="row">
				<div class="col-12 col-md-6">
					<div class="row">
						<div class="col-12">
							<div class="d-flex align-items-end">
								<Input
									readonly
									label={translate('salesOrder.customerName', 'Customer Name')}
									labelParentClass="mb-0 w-100"
									placeholder={translate('salesOrder.customerName', 'Customer Name')}
									value={salesOrder?.customer?.companyName || ''}
								>
									{#snippet append()}
										<Button
											outline
											size="sm"
											class="mr-2"
											iconClass="pen"
											disabled={!salesOrder?.customer}
											onclick={() => {
												asr.go('app.customer', { customerId: salesOrder?.customer?.id }, { inherit: false })
											}}
										/>
									{/snippet}
								</Input>
								<Button
									outline
									size="sm"
									class="ml-2"
									disabled
									iconClass="user"
									onclick={() => customerSearchModal.show()}
								>
									{salesOrder?.customer ? translate('common:change', 'Change') : translate('common:choose', 'Choose')}
								</Button>
							</div>
						</div>
						<div class="col-12 col-lg-6">
							<Input
								readonly
								class="text-right"
								label={translate('common:balance', 'Balance')}
								labelParentClass="mb-0"
								value={salesOrder?.customer?.balance ? formatCustomerCurrency(salesOrder?.customer.balance) : '$0.00'}
							/>
						</div>
						<div class="col-12 col-lg-6">
							<Input
								readonly
								class="text-right"
								label={translate('salesOrder.creditLimit', 'Credit Limit')}
								labelParentClass="mb-0"
								value={salesOrder?.customer?.accountLimit
									? formatCustomerCurrency(salesOrder?.customer.accountLimit)
									: '$0.00'}
							/>
						</div>
						<div class="col-12 col-lg-6">
							<Select
								disabled
								label={translate('salesOrder.counterPerson', 'Counter Person')}
								emptyText={translate('salesOrder.noSalesPerson', 'No Sales Person')}
								value={salesOrder?.salesperson?.id || null}
							>
								{#each salesPersonList as salesperson}
									<option value={salesperson.id}>{salesperson.name}</option>
								{/each}
							</Select>
						</div>
						<div class="col-12 col-lg-6">
							<Select
								disabled
								label={translate('common:taxItem', 'Tax Item')}
								value={salesOrder?.taxItem?.id || null}
							>
								{#each taxItems as taxItem}
									<option value={taxItem.id}>{taxItem.name}</option>
								{/each}
							</Select>
						</div>
					</div>
				</div>
				<div class="col-12 col-md-6">
					<TextArea
						disabled={editingDisabled}
						label={translate('common:internalComments', 'Internal Comments')}
						value={salesOrder?.internalComments || ''}
						onchange={event => {
							if (salesOrder) {
								salesOrder = { ...salesOrder, internalComments: getEventValue(event) }
							}
							salesOrderChanged = true
						}}
					/>
					<TextArea
						disabled={editingDisabled}
						label={translate('salesOrder.externalComments', 'External Comments')}
						value={salesOrder?.externalComments || ''}
						onchange={event => {
							if (salesOrder) {
								salesOrder = { ...salesOrder, externalComments: getEventValue(event) }
							}
							salesOrderChanged = true
						}}
					/>
				</div>
			</div>
		{:else if selectedCustomerDetailTab === 'BILLING_AND_SHIPPING'}
			<div class="row">
				<div class="col-12 col-lg-6">
					<div class="row">
						<div class="col-12 col-lg-6">
							<Input
								disabled={editingDisabled}
								maxlength={20}
								label={translate('common:truckNum', 'Truck #')}
								value={salesOrder?.truckNumber || ''}
								onchange={event => {
									if (salesOrder) {
										salesOrder = { ...salesOrder, truckNumber: getEventValue(event) }
									}
									salesOrderChanged = true
								}}
							/>
						</div>
						<div class="col-12 col-lg-6">
							<Input
								disabled={editingDisabled}
								maxlength={20}
								label={translate('common:purchaseOrderNum', 'Purchase Order #')}
								value={salesOrder?.purchaseOrderNumber || ''}
								onchange={event => {
									if (salesOrder) {
										salesOrder = { ...salesOrder, purchaseOrderNumber: getEventValue(event) }
									}
									salesOrderChanged = true
								}}
							/>
						</div>
						<div class="col-12">
							<div class="card h-100">
								<div class="card-header d-flex justify-content-between align-items-center">
									<h6 class="mb-0">{translate('common:billTo', 'Bill To')}</h6>
									<div>
										<!-- Disabled all these buttons for read-only first version -->
										<!-- For next version: -->
										<!-- TODO: add the validate address component to the validate button -->
										<!-- TODO: also check if the address has been validated, if validated, we can disabled this button -->
										<Button
											outline
											size="sm"
											iconClass="magnifying-glass"
											disabled={true || !salesOrder?.billingAddress}
										>
											{translate('salesOrder.validate', 'Validate')}...
										</Button>
										<Button
											outline
											size="sm"
											iconClass="pen"
											disabled={true || !salesOrder?.billingAddress}
										></Button>
									</div>
								</div>
								<div class="card-body">
									<div class="text-ellipsis">
										{#if salesOrder?.billingAddress.contact}
											{salesOrder?.billingAddress.contact} <br />
										{/if}
										{#if salesOrder?.billingAddress.company}
											{salesOrder?.billingAddress.company} <br />
										{/if}
										{#if salesOrder?.billingAddress.address1}
											{salesOrder?.billingAddress.address1} <br />
										{/if}
										{#if salesOrder?.billingAddress.city}
											{salesOrder?.billingAddress.city},
										{/if}
										<span>{salesOrder?.billingAddress.state} {salesOrder?.billingAddress.zip}</span><br />
										<span>{salesOrder?.billingAddress.country} {salesOrder?.billingAddress.phone}</span>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div class="col-12 col-lg-6">
					<div class="row">
						<div class="col-12 col-lg-6">
							<Select
								disabled
								label={translate('home.salesOrder.shippingMethodColumn', 'Ship Via')}
								value={salesOrder?.shippingMethod?.id || null}
								onchange={event => {
									// This is not needed for version 1 as it is read-only
									// TODO: handle ship via change, if the selected shipping method is "Will Call", we need to disable the shipping tracking number field
								}}
							>
								{#each shippingMethods as method}
									<option value={method.id}>{method.name}</option>
								{/each}
							</Select>
						</div>
						<div class="col-12 col-lg-6">
							<!-- only show this field if "ship via" is not "Will Call" -->
							{#if salesOrder?.shippingMethod?.id !== 19}
								<Input
									readonly
									label={translate('salesOrder.shippingTrackingNum', 'Shipping Tracking #')}
									value={salesOrder?.shippingTrackingNumber || ''}
								/>
							{/if}
						</div>
						<div class="col-12">
							<div class="card h-100">
								<div class="card-header d-flex justify-content-between align-items-center">
									<h6 class="mb-0">{translate('common:shipTo', 'Ship To')}</h6>
									<div>
										<!-- Disabled all these buttons for read-only first version -->
										<!-- For next version: -->
										<!-- TODO: add the validate address component to the validate button -->
										<!-- TODO: also check if the address has been validated, if validated, we can disabled this button -->
										<Button
											outline
											size="sm"
											iconClass="magnifying-glass"
											disabled={true || !salesOrder?.shippingAddress}
										>
											{translate('salesOrder.validate', 'Validate')}...
										</Button>
										{#if editShippingAddressMode}
											<Button
												outline
												size="sm"
												iconClass="check"
												onclick={() => {
													editShippingAddressMode = false
												}}
											>
												{translate('common:done', 'Done')}
											</Button>
										{:else}
											<!-- disabled edit button if ship method is "Will Call" -->
											<Button
												outline
												size="sm"
												iconClass="pen"
												disabled={true || !salesOrder?.shippingAddress || salesOrder?.shippingMethod?.id === 19}
												onclick={() => {
													editShippingAddressMode = true
												}}
											></Button>
										{/if}
									</div>
								</div>
								<div class="card-body">
									{#if editShippingAddressMode}
										<div class="row">
											<div class="col-12">
												<Select
													emptyText={translate('salesOrder.sameAsBillingAddress', 'Same as Billing Address')}
													label={translate('salesOrder.shippingAddress', 'Shipping Address')}
													value={0}
													labelParentClass="mb-3"
												>
													{#each customerShippingAddresses as address}
														<option value={address.id}>{address.label}</option>
													{/each}
												</Select>
											</div>
											<div class="col-12">
												<Input
													showLabel={false}
													placeholder={translate('common:contact', 'Contact')}
													value={salesOrder?.shippingAddress.contact || ''}
												/>
											</div>
											<div class="col-12">
												<Input
													showLabel={false}
													placeholder={translate('common:company', 'Company')}
													value={salesOrder?.shippingAddress.company || ''}
												/>
											</div>
											<div class="col-12">
												<Input
													showLabel={false}
													placeholder={translate('salesOrder.address1', 'Address 1')}
													value={salesOrder?.shippingAddress.address1 || ''}
												/>
											</div>
											<div class="col-12">
												<div class="row">
													<div class="col-6 pr-0">
														<Input
															showLabel={false}
															placeholder={translate('common:city', 'City')}
															value={salesOrder?.shippingAddress.city || ''}
														/>
													</div>
													<div class="col-3 pl-0 pr-0">
														<Input
															showLabel={false}
															placeholder={translate('common:state', 'State')}
															value={salesOrder?.shippingAddress.state || ''}
														/>
													</div>
													<div class="col-3 pl-0">
														<Input
															showLabel={false}
															placeholder={translate('common:zip', 'Zip')}
															value={salesOrder?.shippingAddress.zip || ''}
														/>
													</div>
												</div>
											</div>
										</div>
									{:else}
										<div class="text-ellipsis">
											{#if salesOrder?.shippingAddress.contact}
												{salesOrder?.shippingAddress.contact} <br />
											{/if}
											{#if salesOrder?.shippingAddress.company}
												{salesOrder?.shippingAddress.company} <br />
											{/if}
											{#if salesOrder?.shippingAddress.address1}
												{salesOrder?.shippingAddress.address1} <br />
											{/if}
											{#if salesOrder?.shippingAddress.city}
												{salesOrder?.shippingAddress.city},
											{/if}
											<span>{salesOrder?.shippingAddress.state} {salesOrder?.shippingAddress.zip}</span><br />
											<span>{salesOrder?.shippingAddress.country} {salesOrder?.shippingAddress.phone}</span>
										</div>
									{/if}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		{/if}
	</div>
</div>

<SearchModal
	searchEntity="customer"
	chooseItem={async itemId => {
		await loadSelectedCustomerDetails(itemId)
	}}
	bind:this={customerSearchModal}
></SearchModal>
